import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Pencil, ClipboardCheck, Compass, Hammer, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import { useI18n } from "@/i18n/I18nContext";
import { PAGES } from "@/i18n/pages";

const ICONS = { design: Pencil, consulting: ClipboardCheck, engineering: Compass, manufacturing: Hammer };

const HERO_IMG =
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?fm=jpg&q=80&w=2000&auto=format&fit=crop";

const ConstructionPage = () => {
  const { locale } = useI18n();
  const tr = PAGES[locale]?.construction || PAGES.en.construction;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="construction-page">
      <Navbar />
      <main>
        <section className="relative overflow-hidden hero-wash">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 hover:text-[hsl(var(--primary))]">
                <ArrowLeft className="h-3.5 w-3.5" /> Home
              </Link>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">{tr.eyebrow}</div>
              <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[hsl(var(--primary))]" data-testid="construction-hero-title">
                {tr.hero.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-700">{tr.hero.subtitle}</p>
              <div className="mt-7 flex gap-3">
                <Button asChild size="lg" className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white">
                  <Link to="/#contact">
                    {tr.hero.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/10 blur-2xl" />
                <div className="overflow-hidden rounded-3xl border border-[rgba(201,162,39,0.3)] aspect-[4/5]">
                  <img src={HERO_IMG} alt="Construction site" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-[hsl(var(--tbs-cream-50))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl text-[hsl(var(--primary))]">{tr.services.title}</h2>
              <p className="mt-3 text-slate-700">{tr.services.lead}</p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
              {tr.services.items.map((item, idx) => {
                const Icon = ICONS[item.key] || Hammer;
                return (
                  <motion.div key={item.key} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.45, delay: idx * 0.05 }}>
                    <Card data-testid={`construction-service-${item.key}`} className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-7">
                      <div className="flex items-start justify-between gap-3">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="text-xs font-semibold tracking-wider text-slate-400">0{idx + 1}</span>
                      </div>
                      <h3 className="mt-5 font-display text-2xl text-[hsl(var(--primary))]">{item.title}</h3>
                      <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                      <ul className="mt-5 space-y-2">
                        {item.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-[hsl(var(--primary))] text-white border-0 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[hsl(var(--accent))]/15" />
              <div className="relative">
                <h3 className="font-display text-3xl md:text-4xl">{tr.cta.title}</h3>
                <p className="mt-3 max-w-2xl mx-auto text-white/80">{tr.cta.body}</p>
                <Button asChild size="lg" className="mt-6 h-12 px-6 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--tbs-gold-600))] text-[hsl(var(--primary))] font-semibold">
                  <Link to="/#contact">{tr.cta.button}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default ConstructionPage;
